import { yupResolver } from '@hookform/resolvers/yup'
import { LinePlus } from '@qr-front/icons/line-plus'
import { LineShareAndroid } from '@qr-front/icons/line-share-android'
import { Button } from '@qr-front/ui/components/Button'
import { fieldLabelCls } from '@qr-front/ui/components/InputField'
import { SmartScrollContainer } from '@qr-front/ui/components/SmartScrollContainer'
import { qrTypeAtom } from 'features/generator/generatorState'
import { QR_TYPE } from 'features/generator/generatorTypes'
import { useAtom, useAtomValue } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { Reorder } from 'motion/react'
import { useTranslations } from 'next-intl'
import {
	forwardRef,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react'
import { flushSync } from 'react-dom'
import { FormProvider, useForm } from 'react-hook-form'

import { showNotification } from '@/components/Notification'
import { transformBackendResponseKey } from '@/config'
import { useSyncFieldWithAtom } from '@/hooks'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { validateContentForm } from '@/utils'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../../components/GeneratorFormSection'
import { SOCIAL_PLATFORM_LIST } from '../../utils/socialPlatformsConfig'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import { resetCustomSocialImagesField } from './CustomSocialImageUpload'
import {
	createEmptyCustomSocial,
	CustomSocialLinkFields,
} from './CustomSocialLinkFields'
import { PlatformButton } from './PlatformButton'
import { SocialLinkField } from './SocialLinkField'
import {
	getSocialLinksFormSchema,
	TSocialLinkField,
	TSocialLinksFormFields,
} from './socialLinksFormSchema'

export const SOCIAL_LINKS_FORM_ID = 'social-links'

export const socialLinkListAtom = atomWithReset<TSocialLinkField[]>([])

export const resetSocialLinksForm = () => {
	getStore().set(socialLinkListAtom, RESET)
}

export const SocialLinksForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()
	const formRef = useRef<HTMLFormElement>(null)
	const qrType = useAtomValue(qrTypeAtom) as QR_TYPE | null
	const [isAddingCustomSocial, setIsAddingCustomSocial] =
		useState<boolean>(false)
	const isSocialMediaQrType = qrType === QR_TYPE.SocialMedia
	const isVCardQrType = qrType === QR_TYPE.VCard
	const hasCustomSocialLinks = isSocialMediaQrType || isVCardQrType
	const [socialLinkList, setSocialLinkList] = useAtom(socialLinkListAtom)
	const [draggedSocialLinkList, setDraggedSocialLinkList] = useState<
		TSocialLinkField[] | null
	>(null)
	const orderedSocialLinkList = draggedSocialLinkList ?? socialLinkList

	const form = useForm<TSocialLinksFormFields>({
		defaultValues: {
			socialLinks: getStore().get(socialLinkListAtom),
			customSocialLink: createEmptyCustomSocial(),
		},
		mode: 'onTouched',
		resolver: yupResolver(getSocialLinksFormSchema(t)),
		reValidateMode: 'onChange',
		shouldUnregister: false,
		context: {
			isAddingCustomSocial,
			isSocialMediaQrType,
		},
	})

	const {
		formState: { errors, isValid },
		handleSubmit,
		setFocus,
		setValue,
		trigger,
		clearErrors,
		getFieldState,
		getValues,
	} = form

	useSyncFormSectionValidity({ formId: SOCIAL_LINKS_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				property = property.replace('socials.', 'socialLinks.')
				const translatedMessage = t(transformBackendResponseKey(message))
				form.setError(property as Parameters<typeof form.setError>[0], {
					message: translatedMessage,
				})
			},
			validate: async () => {
				const isValid = await trigger('socialLinks')
				const isCustomSocialValid = await trigger('customSocialLink')
				const socialLinkListLength = getValues('socialLinks').length

				if (!isValid || (isSocialMediaQrType && !isCustomSocialValid)) {
					formRef.current?.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
					})

					if (isSocialMediaQrType && socialLinkListLength === 0) {
						showNotification(
							t('ui__field_validation_errors__generic__socials_min__required', {
								count: 1,
							}),
							{
								type: 'error',
							},
						)
					}

					return { isValid: false }
				}

				return validateContentForm(
					handleSubmit,
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				)
			},
		}),
		[form, getValues, handleSubmit, isSocialMediaQrType, t, trigger],
	)

	useSyncFieldWithAtom({
		atom: socialLinkListAtom,
		setFieldValue: (value) =>
			setValue('socialLinks', value as TSocialLinksFormFields['socialLinks']),
	})

	const handleSocialLinkDragEnd = () => {
		if (!draggedSocialLinkList) return

		flushSync(() => {
			setDraggedSocialLinkList(null)
			setSocialLinkList(draggedSocialLinkList)
		})
		setValue(
			'socialLinks',
			draggedSocialLinkList as TSocialLinksFormFields['socialLinks'],
		)
		if (errors.socialLinks) {
			trigger('socialLinks')
		}
	}

	const selectedPlatformList = useMemo(
		() => socialLinkList.map(({ platformId }) => platformId),
		[socialLinkList],
	)

	const platformButtonList = useMemo(
		() =>
			SOCIAL_PLATFORM_LIST.map(({ id, name, Icon, provider, src }) => {
				const handleClickPlatformButton = () => {
					if (document.activeElement instanceof HTMLElement) {
						document.activeElement.blur()
					}
					const platformAlreadyAdded = socialLinkList.find(
						(field) => field.platformId === id,
					)

					if (!platformAlreadyAdded) {
						const newList = [
							...socialLinkList,
							{
								platformId: id,
								name,
								url: '',
								description: '',
								logo: {
									provider,
									src,
								},
							},
						]
						setSocialLinkList(newList)
						setValue(
							'socialLinks',
							newList as TSocialLinksFormFields['socialLinks'],
						)
						if (newList.length > 0 && hasCustomSocialLinks) {
							const socialLinksMinError = getFieldState('socialLinks').error
							if (socialLinksMinError?.type === 'min') {
								clearErrors('socialLinks')
							}
						}
						setTimeout(
							() => setFocus(`socialLinks.${newList.length - 1}.url`),
							0,
						)
					} else {
						const index = socialLinkList.findIndex(
							(field) => field.platformId === id,
						)
						setSocialLinkList(socialLinkList.filter((_, i) => i !== index))
					}
				}

				const isSelected = selectedPlatformList.includes(id)

				return (
					<li key={id}>
						<PlatformButton
							id={id}
							name={name}
							Icon={Icon}
							isSelected={isSelected}
							onClick={handleClickPlatformButton}
						/>
					</li>
				)
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			selectedPlatformList.length,
			setFocus,
			setSocialLinkList,
			socialLinkList.length,
			trigger,
		],
	)

	return (
		<form ref={formRef}>
			<FormProvider {...form}>
				<label className={fieldLabelCls}>
					{t('generator__content_form_section__social__label')}
				</label>
				<SmartScrollContainer className="mt-2">
					<ul className="flex gap-2 tablet:flex-wrap tablet:gap-4">
						{platformButtonList}
					</ul>
				</SmartScrollContainer>
				{socialLinkList.length > 0 ? (
					<Reorder.Group
						as="ul"
						axis="y"
						values={orderedSocialLinkList}
						onReorder={setDraggedSocialLinkList}
						className="mt-6 flex flex-col gap-2"
					>
						{orderedSocialLinkList.map((socialLink, index) => (
							<SocialLinkField
								key={socialLink.platformId ?? index}
								index={socialLinkList.indexOf(socialLink)}
								isDraggable={socialLinkList.length > 1}
								onDragEnd={handleSocialLinkDragEnd}
							/>
						))}
					</Reorder.Group>
				) : null}
				{isAddingCustomSocial && hasCustomSocialLinks && (
					<CustomSocialLinkFields
						setIsAddingCustomSocial={setIsAddingCustomSocial}
					/>
				)}
				{hasCustomSocialLinks && !isAddingCustomSocial && (
					<Button
						visual="soft"
						className="mt-6 tablet:mt-8 [&>svg]:size-4"
						// Using pointer-down instead of click to avoid button shifting when active input loses focus and validates
						onPointerDown={() => {
							resetCustomSocialImagesField()
							setIsAddingCustomSocial(true)
						}}
					>
						<LinePlus />
						{t('generator__content_form_section__social__add_button')}
					</Button>
				)}
			</FormProvider>
		</form>
	)
})

SocialLinksForm.displayName = 'SocialLinksForm'

export const socialLinksFormConfig = (
	t: TTranslationFunction,
	qrType?: QR_TYPE | null,
): TQrContentFormSection => ({
	id: SOCIAL_LINKS_FORM_ID,
	title:
		qrType === QR_TYPE.SocialMedia
			? t('generator__content_form_section__social__title_required')
			: t('generator__content_form_section__social__title'),
	description:
		qrType === QR_TYPE.SocialMedia
			? t('generator__content_form_section__social_media__social__description')
			: t('generator__content_form_section__social__description'),
	icon: <LineShareAndroid />,
	Content: SocialLinksForm,
})
